import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, AlertTriangle, Zap, Filter, RefreshCw,
  Send, ChevronDown, ChevronUp, ExternalLink, Euro,
  Camera, Music, Sparkles, Utensils, Tent, Palette,
  Target, Check, X, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  P0: { label: 'Urgent', color: 'var(--admin-red)', bg: 'rgba(239,68,68,0.1)', icon: AlertTriangle },
  P1: { label: 'Important', color: 'var(--admin-amber)', bg: 'rgba(245,158,11,0.1)', icon: Zap },
  P2: { label: 'Suggéré', color: 'var(--admin-teal)', bg: 'rgba(93,188,191,0.1)', icon: Sparkles },
};

const CATEGORY_CONFIG = {
  photobooth: { label: 'Photobooth', icon: Camera, color: '#F4A7B2' },
  son_dj: { label: 'Son & DJ', icon: Music, color: 'var(--admin-teal)' },
  gonflable: { label: 'Gonflables', icon: Sparkles, color: 'var(--admin-green)' },
  mobilier: { label: 'Mobilier', icon: Tent, color: 'var(--admin-amber)' },
  food: { label: 'Food & Gourmand', icon: Utensils, color: 'var(--admin-gold)' },
  decoration: { label: 'Décoration', icon: Palette, color: '#F4A7B2' },
  eclairage: { label: 'Éclairage', icon: Zap, color: 'var(--admin-purple)' },
};

const TYPE_LABELS = {
  mariage: '💍 Mariage',
  anniversaire_enfant: '🎂 Anniversaire enfant',
  anniversaire_adulte: '🎉 Anniversaire adulte',
  entreprise: '💼 Entreprise',
  bapteme: '🕊️ Baptême',
  default: '🎪 Événement',
};

function ScoreBadge({ score }) {
  const color = score >= 85 ? '#1F9D7A' : score >= 65 ? '#D4AF37' : score >= 40 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: color }} />
      </div>
      <span className="text-xs font-bold" style={{ color }}>{score}%</span>
    </div>
  );
}

export default function AdminOpportunites() {
  const [data, setData] = useState({ opportunites: [], total: 0, total_potentiel_min: 0, categories: {} });
  const [loading, setLoading] = useState(true);
  const [filtrePriorite, setFiltrePriorite] = useState('all');
  const [filtreCategorie, setFiltreCategorie] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [sending, setSending] = useState(null);
  const [editingMsg, setEditingMsg] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const token = getToken();
      let url = `${BACKEND}/api/admin/opportunites?limit=100`;
      if (filtrePriorite !== 'all') url += `&priorite=${filtrePriorite}`;
      if (filtreCategorie !== 'all') url += `&categorie=${filtreCategorie}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      setData(json);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filtrePriorite, filtreCategorie]);

  useEffect(() => { load(); }, [load]);

  const handleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
    if (!editingMsg[id]) {
      const opp = data.opportunites.find(o => o.id === id);
      if (opp) setEditingMsg(prev => ({ ...prev, [id]: opp.template_message }));
    }
  };

  const sendProposition = async (opp) => {
    setSending(opp.id);
    try {
      const token = getToken();
      const res = await fetch(`${BACKEND}/api/admin/opportunites/proposition`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote_id: opp.quote_id,
          client_email: opp.client_email,
          client_nom: opp.client_nom,
          opportunite_id: opp.id,
          message: editingMsg[opp.id] || opp.template_message,
          subject: `Une suggestion pour votre événement — Show Événement`,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Proposition envoyée à ${opp.client_nom} !`);
      setExpanded(null);
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(null);
    }
  };

  const p0Count = data.opportunites.filter(o => o.priorite === 'P0').length;
  const p1Count = data.opportunites.filter(o => o.priorite === 'P1').length;

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Opportunités commerciales</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Suggestions de relance et d'upsell</p>
      </div>
      <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(239,68,68,0.12)' }}>
              <Target size={20} style={{ color: 'var(--admin-red)' }} />
            </div>
            <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>
              Opportunités détectées
            </h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>
            Analyse automatique des besoins manquants chez vos clients
          </p>
        </div>
        <button onClick={() => load(false)} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium"
          style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
          <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          Actualiser
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total opportunités', value: data.total, color: 'var(--admin-teal)', icon: TrendingUp },
          { label: '🔴 Urgentes (P0)', value: p0Count, color: 'var(--admin-red)', icon: AlertTriangle },
          { label: '🟡 Importantes (P1)', value: p1Count, color: 'var(--admin-amber)', icon: Zap },
          { label: 'Potentiel min.', value: `${data.total_potentiel_min?.toFixed(0) || 0}€`, color: 'var(--admin-gold)', icon: Euro },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="card-premium p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon size={14} style={{ color: s.color }} />
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{s.label}</p>
              </div>
              <p className="font-display text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          );
        })}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 mb-5">
        <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>
          <Filter size={12} /> Priorité :
        </div>
        {['all', 'P0', 'P1', 'P2'].map(p => (
          <button key={p} onClick={() => setFiltrePriorite(p)}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: filtrePriorite === p ? (PRIORITY_CONFIG[p]?.color || '#5DBCBF') : 'var(--bg)',
              color: filtrePriorite === p ? '#FFFFFF' : 'var(--text-muted)',
              border: `1px solid ${filtrePriorite === p ? (PRIORITY_CONFIG[p]?.color || '#5DBCBF') : 'var(--border)'}`,
              cursor: 'pointer',
            }}>
            {p === 'all' ? 'Toutes' : PRIORITY_CONFIG[p]?.label}
          </button>
        ))}
        <div className="flex items-center gap-1 text-xs font-semibold ml-2" style={{ color: 'var(--admin-muted)' }}>
          Catégorie :
        </div>
        {['all', ...Object.keys(CATEGORY_CONFIG)].map(c => (
          <button key={c} onClick={() => setFiltreCategorie(c)}
            className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: filtreCategorie === c ? (CATEGORY_CONFIG[c]?.color || '#5DBCBF') + '22' : 'var(--bg)',
              color: filtreCategorie === c ? (CATEGORY_CONFIG[c]?.color || '#5DBCBF') : 'var(--text-muted)',
              border: `1px solid ${filtreCategorie === c ? (CATEGORY_CONFIG[c]?.color || '#5DBCBF') : 'var(--border)'}`,
              cursor: 'pointer',
            }}>
            {c === 'all' ? 'Toutes' : CATEGORY_CONFIG[c]?.label}
          </button>
        ))}
      </div>

      {/* Liste opportunités */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />
          ))}
        </div>
      ) : data.opportunites.length === 0 ? (
        <div className="text-center py-16">
          <Check size={40} style={{ color: 'var(--admin-green)', margin: '0 auto 12px' }} />
          <p className="font-semibold" style={{ color: 'var(--admin-text)' }}>Aucune opportunité détectée</p>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Tous vos devis semblent complets !</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.opportunites.map(opp => {
            const pConfig = PRIORITY_CONFIG[opp.priorite] || PRIORITY_CONFIG.P2;
            const cConfig = CATEGORY_CONFIG[opp.categorie] || { label: opp.categorie, icon: Sparkles, color: '#888' };
            const CatIcon = cConfig.icon;
            const PriorityIcon = pConfig.icon;
            const isOpen = expanded === opp.id;

            return (
              <div key={opp.id} className="rounded-2xl overflow-hidden"
                style={{ border: `1px solid ${isOpen ? pConfig.color + '40' : 'var(--border)'}`, background: '#FFFFFF', boxShadow: isOpen ? `0 4px 20px ${pConfig.color}15` : 'none' }}>

                {/* En-tête */}
                <button onClick={() => handleExpand(opp.id)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                  style={{ cursor: 'pointer', background: 'none', border: 'none' }}>

                  {/* Priorité */}
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: pConfig.bg }}>
                    <PriorityIcon size={15} style={{ color: pConfig.color }} />
                  </div>

                  {/* Info principale */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>
                        {opp.client_nom}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: pConfig.bg, color: pConfig.color }}>
                        {pConfig.label}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: `${cConfig.color}18`, color: cConfig.color }}>
                        <CatIcon size={10} className="inline mr-1" />
                        {cConfig.label}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--admin-muted)' }}>
                        {TYPE_LABELS[opp.type_event] || TYPE_LABELS.default}
                      </span>
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>
                      {opp.description}
                    </p>
                  </div>

                  {/* Méta droite */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>
                        {opp.potentiel_min}–{opp.potentiel_max}€
                      </p>
                      <ScoreBadge score={opp.score_event || 0} />
                    </div>
                    {isOpen ? <ChevronUp size={16} style={{ color: 'var(--admin-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--admin-muted)' }} />}
                  </div>
                </button>

                {/* Contenu développé */}
                {isOpen && (
                  <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--admin-border)' }}>
                    <div className="pt-4 grid sm:grid-cols-3 gap-4">

                      {/* Suggestions produits */}
                      <div>
                        <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-text)' }}>
                          💡 Suggestions produits
                        </p>
                        <div className="space-y-1.5">
                          {opp.suggestions?.map((s, i) => (
                            <a key={i} href={s.href} target="_blank" rel="noreferrer"
                              className="flex items-center justify-between p-2 rounded-xl text-xs hover:underline"
                              style={{ background: 'var(--admin-bg)', color: 'var(--admin-text)' }}>
                              <span>{s.nom}</span>
                              <span className="font-bold" style={{ color: 'var(--admin-gold)' }}>{s.prix}€</span>
                            </a>
                          ))}
                        </div>

                        {/* Infos client */}
                        <div className="mt-3 p-2 rounded-xl text-xs" style={{ background: 'var(--admin-bg)' }}>
                          <div className="flex items-center gap-1 mb-0.5" style={{ color: 'var(--admin-muted)' }}>
                            <Users size={10} /> {opp.nb_invites || '?'} invités
                          </div>
                          {opp.date_event && (
                            <div className="flex items-center gap-1" style={{ color: 'var(--admin-muted)' }}>
                              <Clock size={10} /> {opp.date_event}
                            </div>
                          )}
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs">Score : </span>
                            <ScoreBadge score={opp.score_event || 0} />
                          </div>
                        </div>
                      </div>

                      {/* Message éditable */}
                      <div className="sm:col-span-2">
                        <p className="text-xs font-bold mb-2" style={{ color: 'var(--admin-text)' }}>
                          ✉️ Message à envoyer (modifiable)
                        </p>
                        <textarea
                          rows={7}
                          value={editingMsg[opp.id] || ''}
                          onChange={e => setEditingMsg(prev => ({ ...prev, [opp.id]: e.target.value }))}
                          className="w-full px-3 py-2 rounded-xl text-xs resize-none"
                          style={{ border: '1px solid var(--admin-border)', background: '#FFFFFF', color: 'var(--admin-text)', outline: 'none', lineHeight: '1.6' }}
                        />
                        {opp.client_email ? (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => sendProposition(opp)}
                              disabled={sending === opp.id}
                              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold flex-1 justify-center"
                              style={{ background: '#D4AF37', color: '#0B1E20', border: 'none', cursor: 'pointer', opacity: sending === opp.id ? 0.7 : 1 }}>
                              <Send size={13} />
                              {sending === opp.id ? 'Envoi…' : `Envoyer à ${opp.client_email}`}
                            </button>
                            <a href={`/admin/devis?q=${opp.quote_id}`}
                              className="flex items-center gap-1 px-3 py-2.5 rounded-xl text-xs font-semibold"
                              style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', textDecoration: 'none' }}>
                              <ExternalLink size={12} /> Voir devis
                            </a>
                          </div>
                        ) : (
                          <p className="text-xs mt-2" style={{ color: 'var(--admin-muted)' }}>
                            ⚠️ Pas d'email client enregistré — ouvrez le devis pour contacter.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
